import React, { useState } from "react";
import { View, Text, StyleSheet, Image, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme } from "../../../context/ThemeContext";

const UpdateCard = ({ update }) => {
	const theme = useTheme();
	const styles = useStyles();
	const timeAgo = new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(Math.ceil((new Date(update.timestamp) - new Date()) / (1000 * 60 * 60 * 24)), "day");

	const renderContent = () => {
		switch (update.type) {
			case "goal_progress":
				return (
					<View style={styles.progressContent}>
						<View style={styles.progressBar}>
							<View style={[styles.progressFill, { width: "23%" }]} />
						</View>
						<Text style={styles.progressText}>{update.content}</Text>
					</View>
				);
			case "reading_update":
				return (
					<View style={styles.readingContent}>
						<Text style={styles.updateText}>{update.content}</Text>
					</View>
				);
			case "poll":
				return (
					<View style={styles.pollContent}>
						<Text style={styles.pollQuestion}>{update.content}</Text>
						<View style={styles.pollOptions}>
							{update.options.map((option, index) => {
								const percentage = Math.round((option.votes / update.totalVotes) * 100);
								return (
									<View key={index} style={styles.pollOption}>
										<View style={styles.pollBar}>
											<View style={[styles.pollFill, { width: `${percentage}%` }]} />
											<Text style={styles.optionText}>{option.text}</Text>
										</View>
										<Text style={styles.voteCount}>{option.votes} votes</Text>
									</View>
								);
							})}
						</View>
					</View>
				);
			default:
				return null;
		}
	};

	return (
		<View style={styles.updateCard}>
			<View style={styles.updateHeader}>
				<View style={styles.userInfo}>
					<Image source={{ uri: update.userAvatar }} style={styles.avatar} />
					<View>
						<Text style={styles.userName}>{update.userName}</Text>
						<Text style={styles.timestamp}>{timeAgo}</Text>
					</View>
				</View>
				<TouchableOpacity style={styles.moreButton}>
					<Ionicons name="ellipsis-horizontal" size={20} color={theme.colors.textSecondary} />
				</TouchableOpacity>
			</View>

			{renderContent()}

			<View style={styles.updateActions}>
				<TouchableOpacity style={styles.actionButton}>
					<Ionicons name="heart-outline" size={20} color={theme.colors.textSecondary} />
					<Text style={styles.actionText}>{update.likes}</Text>
				</TouchableOpacity>
				<TouchableOpacity style={styles.actionButton}>
					<Ionicons name="chatbubble-outline" size={20} color={theme.colors.textSecondary} />
					<Text style={styles.actionText}>{update.comments}</Text>
				</TouchableOpacity>
				<TouchableOpacity style={styles.actionButton}>
					<Ionicons name="share-social-outline" size={20} color={theme.colors.textSecondary} />
				</TouchableOpacity>
			</View>
		</View>
	);
};

const ReadingUpdates = ({ updates = [] }) => {
	const theme = useTheme();
	const styles = useStyles();
	const [filter, setFilter] = useState("all");

	const filters = [
		{ id: "all", label: "All Updates" },
		{ id: "goals", label: "Reading Goals" },
		{ id: "reading", label: "Currently Reading" },
		{ id: "polls", label: "Book Polls" },
	];

	const filteredUpdates = updates.filter((update) => {
		switch (filter) {
			case "goals":
				return update.type === "goal_progress";
			case "reading":
				return update.type === "reading_update";
			case "polls":
				return update.type === "poll";
			default:
				return true;
		}
	});

	return (
		<View style={styles.container}>
			<View style={styles.header}>
				<View style={styles.filters}>
					{filters.map((f) => (
						<TouchableOpacity key={f.id} style={[styles.filterButton, filter === f.id && styles.activeFilter]} onPress={() => setFilter(f.id)}>
							<Text style={[styles.filterText, filter === f.id && styles.activeFilterText]}>{f.label}</Text>
						</TouchableOpacity>
					))}
				</View>
				<TouchableOpacity style={styles.newUpdateButton}>
					<Ionicons name="add" size={20} color={theme.colors.systemBackground.primary} />
					<Text style={styles.newUpdateText}>Share Update</Text>
				</TouchableOpacity>
			</View>

			<View style={styles.updatesList}>
				{filteredUpdates.map((update) => (
					<UpdateCard key={update.id} update={update} />
				))}
			</View>
		</View>
	);
};

const useStyles = () => {
	const theme = useTheme();

	return StyleSheet.create({
		container: {
			flex: 1,
		},
		header: {
			marginBottom: theme.spacing.md,
		},
		filters: {
			flexDirection: "row",
			flexWrap: "wrap",
			gap: theme.spacing.sm,
			marginBottom: theme.spacing.md,
		},
		filterButton: {
			paddingVertical: theme.spacing.xs,
			paddingHorizontal: theme.spacing.sm,
			borderRadius: theme.borderRadius.pill,
			backgroundColor: theme.colors.systemBackground.primary,
		},
		activeFilter: {
			backgroundColor: theme.colors.primary,
		},
		filterText: {
			...theme.typography.footnote,
			color: theme.colors.textSecondary,
		},
		activeFilterText: {
			color: theme.colors.systemBackground.primary,
			fontWeight: "600",
		},
		newUpdateButton: {
			flexDirection: "row",
			alignItems: "center",
			backgroundColor: theme.colors.primary,
			borderRadius: theme.borderRadius.md,
			padding: theme.spacing.sm,
			alignSelf: "flex-start",
			gap: theme.spacing.sm,
		},
		newUpdateText: {
			...theme.typography.subhead,
			color: theme.colors.systemBackground.primary,
			fontWeight: "600",
		},
		updatesList: {
			gap: theme.spacing.md,
		},
		updateCard: {
			backgroundColor: theme.colors.systemBackground.primary,
			borderRadius: theme.borderRadius.lg,
			padding: theme.spacing.md,
			...theme.shadows.sm,
		},
		updateHeader: {
			flexDirection: "row",
			alignItems: "center",
			justifyContent: "space-between",
			marginBottom: theme.spacing.md,
		},
		userInfo: {
			flexDirection: "row",
			alignItems: "center",
			gap: theme.spacing.md,
		},
		avatar: {
			width: 40,
			height: 40,
			borderRadius: 20,
		},
		userName: {
			...theme.typography.headline,
			color: theme.colors.text,
		},
		timestamp: {
			...theme.typography.caption1,
			color: theme.colors.textSecondary,
		},
		moreButton: {
			padding: theme.spacing.xs,
		},
		progressContent: {
			marginBottom: theme.spacing.md,
		},
		progressBar: {
			height: 8,
			backgroundColor: theme.colors.gray[6],
			borderRadius: theme.borderRadius.pill,
			marginBottom: theme.spacing.sm,
		},
		progressFill: {
			height: "100%",
			backgroundColor: theme.colors.primary,
			borderRadius: theme.borderRadius.pill,
		},
		progressText: {
			...theme.typography.body,
			color: theme.colors.text,
		},
		readingContent: {
			marginBottom: theme.spacing.md,
		},
		updateText: {
			...theme.typography.body,
			color: theme.colors.text,
			lineHeight: 24,
		},
		pollContent: {
			marginBottom: theme.spacing.md,
		},
		pollQuestion: {
			...theme.typography.headline,
			color: theme.colors.text,
			marginBottom: theme.spacing.md,
		},
		pollOptions: {
			gap: theme.spacing.md,
		},
		pollOption: {
			gap: theme.spacing.xs,
		},
		pollBar: {
			height: 36,
			backgroundColor: theme.colors.gray[6],
			borderRadius: theme.borderRadius.md,
			overflow: "hidden",
			position: "relative",
		},
		pollFill: {
			position: "absolute",
			top: 0,
			left: 0,
			bottom: 0,
			backgroundColor: theme.colors.gray[5],
		},
		optionText: {
			position: "absolute",
			left: theme.spacing.md,
			top: "50%",
			transform: [{ translateY: -8 }],
			...theme.typography.subhead,
			color: theme.colors.text,
			zIndex: 1,
		},
		voteCount: {
			...theme.typography.caption1,
			color: theme.colors.textSecondary,
			marginLeft: theme.spacing.md,
		},
		updateActions: {
			flexDirection: "row",
			gap: theme.spacing.md,
			paddingTop: theme.spacing.md,
			borderTopWidth: 1,
			borderTopColor: theme.colors.separator,
		},
		actionButton: {
			flexDirection: "row",
			alignItems: "center",
			gap: theme.spacing.xs,
		},
		actionText: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
		},
	});
};

export default ReadingUpdates;
